import { catchError, firstValueFrom, from, of } from "rxjs";
import { inject, Injectable } from "@angular/core";
import {
  ICreateVoucherRequestParams,
  ICreateVoucherResult,
  IExecuteProcedureRequest,
  IExecuteProcedureResult,
  IFEConfigResultData,
  IFilteredHeaderPayments,
  IGetPaymentTypesResultData,
  IGetTripRequestParams,
  IHeadersWithPaymentsPayment,
  IHeadersWithPaymentsResultData,
  IInsertDertHeadersResultData,
  ITripPayment,
  IVoucherCreateResponseData,
  IVoucherStylesResponseData,
  ResultCode,
} from "./model/pos.api.model";
import {
  IBlobExportData,
  IGetSobitTokenParams,
  IGetUpdateDertPaymentParams,
  IInsertHeaderRecordParams,
  ITaxItem,
  ITaxItemParams,
} from "./model/sp-procedures-params.model";
import { ProcedureNameEnum } from "../enums/procedure-name.enum";
import { DbSchemeEnum } from "../enums/db-scheme.enum";
import { SharedApiService } from "./api/sharedApi.service";
import { PaymentStatusEnum } from "../enums/payment-status.enum";
import { QueryParamEnum } from "../enums/query-param.enum";
import { PopUpDialogInformalService } from "./confirms/popUpDialogInformal.service";
import { PopUpInformalType } from "../components/ui-components/pop-ups/pop-up-informal/pop-up-informal.model";
import { TranslateService } from "@ngx-translate/core";
import { Router } from "@angular/router";
import { StateService } from "./state.service";
import { DropdownOption } from "../components/ui-components/dropdown/dropdown.model";
import { TenderType } from "./model/tender-type.enum";
import { LoginService } from "./login.service";

@Injectable({
  providedIn: "root",
})
export class SharedService {
  private _sharedApiService: SharedApiService = inject(SharedApiService);
  private translate = inject(TranslateService);
  private _popUpDialogInformalService: PopUpDialogInformalService = inject(PopUpDialogInformalService);
  private _router = inject(Router);
  private _stateService = inject(StateService);
  private _loginService = inject(LoginService);
  private _paymentTypesCache: IGetPaymentTypesResultData[] | null = null;
  private _paymentTypesCacheKey = "";
  private _paymentTypesCacheVersion = 0;
  private _paymentTypesCachePromise: Promise<IGetPaymentTypesResultData[] | null> | null = null;

  setQueryParamToUrl(paramName: QueryParamEnum, value: string): void {
    const url = new URL(window.location.href);
    url.searchParams.set(paramName, value);
    window.history.pushState({}, "", url.toString());
  }

  getQueryParamFromUrl(paramName: QueryParamEnum): string | null {
    const url = new URL(window.location.href);
    return url.searchParams.get(paramName);
  }

  async loadFEConfig(): Promise<boolean> {
    let country = localStorage.getItem("countryCode");
    country = country ? country : "cz";

    const request = {
      Parameters: {
        Country: country,
      },
      ProcedureName: ProcedureNameEnum.GetFEConfig,
      DbScheme: DbSchemeEnum.Dert,
    };
    const response: IExecuteProcedureResult<IFEConfigResultData> =
      await this._sharedApiService.executeProcedure(request);
    if (!response || response.result !== ResultCode.OK) {
      await this._popUpDialogInformalService.confirm({
        title: this.translate.instant("webPos.Errors.Error"),
        message: this.translate.instant("webPos.Errors.GetConfigurationError"),
        type: PopUpInformalType.Error,
      });
      return false;
    }

    this._stateService.setConfig(response.data);
    return true;
  }

  checkDocumentPayments(data: IHeadersWithPaymentsResultData[]): IFilteredHeaderPayments {
    const pendingPayments = [];
    const donePayments = [];
    const failedPayments = [];
    const allPayments: Array<ITripPayment> = [];

    data.forEach((header) => {
      if (!header.Payments || header.Payments.length === 0) {
        return;
      }

      /**
       * Processes each payment in a given header and sorts them into different categories based on their status
       * (e.g., Pending, Done, Cancelled, Aborted), warning if an unknown status is encountered.
       * Each payment, regardless of status, is added to the `allPayments` array for consolidated tracking.
       * Includes detailed logging for unknown payment statuses.
       * @param {IHeadersWithPaymentsPayment} payment - An individual payment object from the array of payments associated with a header.
       * @returns {void} No return value. Modifies the pendingPayments, donePayments, failedPayments, and allPayments arrays in the class scope.
       */
      header.Payments.forEach((payment: IHeadersWithPaymentsPayment) => {
        switch (payment.Status) {
          case PaymentStatusEnum.Pending:
            pendingPayments.push(payment);
            break;
          case PaymentStatusEnum.Done:
            donePayments.push(payment);
            break;
          case PaymentStatusEnum.Cancelled:
          case PaymentStatusEnum.WrongCredentials:
            failedPayments.push(payment);
            break;
          case PaymentStatusEnum.Aborted:
          case PaymentStatusEnum.TerminalBusy:
          case PaymentStatusEnum.CancellationNotFound:
            failedPayments.push(payment);
            break;
          case PaymentStatusEnum.NotFound:
            failedPayments.push(payment);
            break;
          default:
            console.warn("Unknown payment status:", payment.Status);
        }
        allPayments.push({
          amount: payment.PaymentValue,
          date: payment.Created,
          id: payment.PaymentUID,
          type: payment.PaymentType,
          currency: payment.CurrencyISOCode,
          status: payment.Status,
        } as ITripPayment);
      });
    });

    return {
      pendingPayments: pendingPayments,
      donePayments: donePayments,
      failedPayments: failedPayments,
      allPayments: allPayments,
    };
  }

  getSobitTokenRequest(): IExecuteProcedureRequest<IGetSobitTokenParams> {
    const params: IGetSobitTokenParams = {  };
    const getSobitTokenRequest: IExecuteProcedureRequest<IGetSobitTokenParams> =
      {
        Parameters: params,
        ProcedureName: ProcedureNameEnum.GetSobitToken,
        DbScheme: DbSchemeEnum.Dert,
      };

    return getSobitTokenRequest;
  }

  getTaxItems(): IExecuteProcedureRequest<ITaxItemParams> {
    const params: ITaxItemParams = {
      CountryCode: "CZ", // e.g. "CZ" TODO: get country from user
    };
    const getTaxItemsRequest: IExecuteProcedureRequest<ITaxItemParams> =
    {
      Parameters: params,
      ProcedureName: ProcedureNameEnum.GetTaxList,
      DbScheme: DbSchemeEnum.Dert,
    };

    return getTaxItemsRequest;
  }

  getUpdateDertPaymentRequest(
    status: number,
    paymentUID: string,
    data: any[] // Mapped from Sobit response, we will not change type if Sobit change it
  ): IExecuteProcedureRequest<IGetUpdateDertPaymentParams> {
    const params: IGetUpdateDertPaymentParams = {
      PaymentUID: paymentUID,
      Status: status,
      Data: data,
    };
    const getUpdateDertPaymentRequest: IExecuteProcedureRequest<IGetUpdateDertPaymentParams> =
      {
        Parameters: params,
        ProcedureName: ProcedureNameEnum.UpdateDertPayments,
        DbScheme: DbSchemeEnum.Dert,
      };

    return getUpdateDertPaymentRequest;
  }

  async createPaymentRecord(
   model: IInsertHeaderRecordParams
  ): Promise<IExecuteProcedureResult<IInsertDertHeadersResultData>> {
    const insertDertHeadersRequest = this.getInsertDertHeadersRequest(
      model
    );
    const createHeaderRecResult: IExecuteProcedureResult<IInsertDertHeadersResultData> =
      await this._sharedApiService.executeProcedure(insertDertHeadersRequest);

    return createHeaderRecResult;
  }

  async getBenefityTypes(): Promise<IGetPaymentTypesResultData[]> {
    const paymentTypes = await this.getPaymentTypes();
    return paymentTypes.filter(pt => !pt.IncludedItemAssortmentCode);
  }

  async getOtherTypes(): Promise<IGetPaymentTypesResultData[]> {
    const paymentTypes = await this.getPaymentTypes();
    return paymentTypes.filter(pt => pt.IncludedItemAssortmentCode === "OTHERS");
  }

  async getPaymentTypes(): Promise<IGetPaymentTypesResultData[]> {
    const paymentTypes = await this.getAllPaymentTypes();
    return paymentTypes.filter(paymentType => paymentType.IsPayAllowed === true);
  }

  async getAllPaymentTypes(forceRefresh = false): Promise<IGetPaymentTypesResultData[]> {
    const key = `${this._loginService.user?.SelectedBusinessUnit ?? ""}:${this._loginService.user?.CurrencyCode ?? ""}`;
    if (key !== this._paymentTypesCacheKey) {
      this._paymentTypesCacheKey = key;
      this._paymentTypesCacheVersion++;
      this._paymentTypesCache = null;
      this._paymentTypesCachePromise = null;
    }
    if (this._paymentTypesCachePromise) return this._paymentTypesCachePromise;
    if (!forceRefresh && this._paymentTypesCache) return this._paymentTypesCache;

    const version = this._paymentTypesCacheVersion;
    const request = firstValueFrom(from(this.fetchPaymentTypes()).pipe(
      catchError(() => of(null as IGetPaymentTypesResultData[] | null)),
    ));
    this._paymentTypesCachePromise = request;
    const payments = await request;
    if (version !== this._paymentTypesCacheVersion) return [];
    this._paymentTypesCache = payments;
    this._paymentTypesCachePromise = null;
    return payments ?? [];
  }

  // Get current cash payment type respecting currency and use it to get available cash for return payment dialog
  async getCashPaymentTypeForCurrency(): Promise<IGetPaymentTypesResultData | null> {
    // load currecy code from user data and find cash payment type for this currency
    const currencyCode = this._loginService.user.CurrencyCode;
    if (!currencyCode) {
      console.error('User currency code is not available.');
      return null;
    }
    const paymentTypes = await this.getPaymentTypes();
    const cashPaymentType = paymentTypes.find(pt => pt.TenderType === TenderType.Cash && pt.Currency === currencyCode);
    return cashPaymentType || null;
  }

  private async fetchPaymentTypes(): Promise<IGetPaymentTypesResultData[] | null> {
    const getPaymentTypesRequest: IExecuteProcedureRequest<any> = {
      Parameters: {
        SelectedBusinessUnit: this._loginService.user?.SelectedBusinessUnit ?? null,
        CurrencyISOCode: this._loginService.user?.CurrencyCode ?? null,
      },
      ProcedureName: ProcedureNameEnum.GetPaymentTypes,
      DbScheme: DbSchemeEnum.Dert,
    };

    const result: IExecuteProcedureResult<IGetPaymentTypesResultData[]> =
      await this._sharedApiService.executeProcedure(getPaymentTypesRequest);

    if (!result || result.result !== ResultCode.OK) {
      await this._popUpDialogInformalService.confirm({
        title: this.translate.instant("webPos.Errors.Error"),
        message: this.translate.instant("webPos.Errors.ServerError"),
        type: PopUpInformalType.Error,
      });
      this.navigateToDashboard();
      return null;
    }

    if (!result.data) {
      await this._popUpDialogInformalService.confirm({
        title: this.translate.instant("webPos.Errors.Error"),
        message: this.translate.instant("webPos.Errors.NoPaymentTypesFound"),
        type: PopUpInformalType.Error,
      });
      return null;
    }

    return result.data;
  }

  getFormattedBenefitData(benefitData: IGetPaymentTypesResultData[]): DropdownOption[] {
    const formattedData: DropdownOption[] = [];
    benefitData.forEach((item) => { formattedData.push({ label: item.Description, value: item.Name }); });
    return formattedData;
  }

  getBenefitType(benefit: string, benefitData: IGetPaymentTypesResultData[]): TenderType | null 
  {
    return benefitData.find((item) => item.Name === benefit)?.TenderType || null;
  }

  getBenefitNominals(benefit: string, benefitData: IGetPaymentTypesResultData[]): DropdownOption[] {
    const selectedBenefit = benefitData.find((item) => item.Name === benefit);
    const nominals = selectedBenefit?.Nominals
      ? [...selectedBenefit.Nominals].sort((a, b) => a.sequence - b.sequence)
      : [];

    if (!nominals.length) {
      return [];
    }

    const nominalOptions = nominals.map((nominal) => {
      return { label: nominal.denomination, value: nominal.value };
    });
    return nominalOptions;
  }

  prepareExportData() {}

  async getTaxList(): Promise<IExecuteProcedureResult<ITaxItem[]>> {
    const getTaxItemsRequest = this.getTaxItems();

    const res: IExecuteProcedureResult<ITaxItem[]> =
      await this._sharedApiService.executeProcedure(getTaxItemsRequest);

    // BAD REQUEST
    if (res?.result === ResultCode.OK) {
      return res;
    }
    return null;
  }

  private getInsertDertHeadersRequest(model: IInsertHeaderRecordParams): IExecuteProcedureRequest<IInsertHeaderRecordParams> {
    const insertDertHeadersRequest: IExecuteProcedureRequest<IInsertHeaderRecordParams> =
      {
        Parameters: model,
        ProcedureName: ProcedureNameEnum.InsertDertHeaders,
        DbScheme: DbSchemeEnum.Dert,
      };

    return insertDertHeadersRequest;
  }

  public navigateToDashboard() {
    this._router.navigate(["dashboard"]);
  }

  public async throwErrorDialog(
    message: string,
    subMessage: string | null = null,
    goToDashboard = false
  ) {
    await this._popUpDialogInformalService.confirm({
      title: this.translate.instant("webPos.Errors.Error"),
      message: message,
      subMessage: subMessage ? subMessage : null,
      type: PopUpInformalType.Error,
    });

    if (goToDashboard) {
      this.navigateToDashboard();
    }
  }
}
